import React from 'react';
import styles from './Timeline.module.scss';
import { Spinner } from 'reactstrap';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button } from 'reactstrap';
import moment from 'moment-timezone';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import {
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
} from '@/utils/screenshotPrivacy';

export const TimelineScreenshotsModal = ({
  activeSessionMeta,
  activitiesLoading,
  activitiesError,
  flattenedScreens,
  selectedScreenshotIndex,
  selectedTimezone,
  setSelectedScreenshotIndex,
}) => {
  const selectPrev = () => {
    if (!flattenedScreens.length) return;
    setSelectedScreenshotIndex((prev) =>
      prev === 0 ? flattenedScreens.length - 1 : prev - 1
    );
  };

  const selectNext = () => {
    if (!flattenedScreens.length) return;
    setSelectedScreenshotIndex((prev) =>
      prev === flattenedScreens.length - 1 ? 0 : prev + 1
    );
  };

  const currentShot = flattenedScreens[selectedScreenshotIndex];
  const mainSrc = getScreenshotFullSrc(currentShot);

  return (
    <div className={styles.timelineScreenshotsModal}>
      <Text variant={textVariantsEnum.Text_13} fontWeight={fontWeightEnum.Bold}>
        Screenshots{' '}
        {activeSessionMeta?.dayLabel ? `- ${activeSessionMeta.dayLabel}` : ''}
      </Text>
      {activitiesLoading && (
        <div className="text-center my-3">
          <Spinner color="primary" />
        </div>
      )}
      {!activitiesLoading && activitiesError && (
        <div className="text-danger">{activitiesError}</div>
      )}
      {!activitiesLoading &&
        !activitiesError &&
        flattenedScreens.length === 0 && (
          <div className="text-muted">
            No screenshots available for this session.
          </div>
        )}
      {!activitiesLoading &&
        !activitiesError &&
        flattenedScreens.length > 0 && (
          <div>
            <div
              className="d-flex align-items-center justify-content-center mb-3 position-relative"
              style={{
                minHeight: '280px',
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '12px',
              }}
            >
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={selectPrev}
                style={{
                  position: 'absolute',
                  left: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '44px',
                  height: '44px',
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  border: 'none',
                }}
                aria-label="Previous screenshot"
              >
                <FiChevronLeft size={22} />
              </Button>
              <div className="text-center w-100">
                <img
                  src={mainSrc}
                  alt="Screenshot preview"
                  style={{
                    maxHeight: '60vh',
                    maxWidth: '100%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                />
                <div className="mt-2">
                  <div className="font-weight-bold">
                    {flattenedScreens[selectedScreenshotIndex]?.timestamp
                      ? moment(
                          flattenedScreens[selectedScreenshotIndex].timestamp
                        )
                          .tz(selectedTimezone)
                          .format('DD MMM YYYY, hh:mm A')
                      : 'Timestamp unavailable'}
                  </div>
                  <small className="text-muted">
                    {`Screenshot ${selectedScreenshotIndex + 1} of ${
                      flattenedScreens.length
                    }`}
                  </small>
                  <div>
                    <a
                      href={getScreenshotFullSrc(
                        flattenedScreens[selectedScreenshotIndex]
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in new tab
                    </a>
                  </div>
                </div>
              </div>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={selectNext}
                style={{
                  position: 'absolute',
                  right: -12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '44px',
                  height: '44px',
                  padding: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(0,0,0,0.55)',
                  color: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  border: 'none',
                }}
                aria-label="Next screenshot"
              >
                <FiChevronRight size={22} />
              </Button>
            </div>
            <div>
              <div className="mb-2 font-weight-bold">All screenshots</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                }}
              >
                {flattenedScreens.map((item, idx) => (
                  <div
                    key={`${item.activityId || 'activity'}-${idx}`}
                    className="text-center"
                    style={{
                      border:
                        idx === selectedScreenshotIndex
                          ? '2px solid #007bff'
                          : '1px solid #e9ecef',
                      borderRadius: '6px',
                      padding: '6px',
                      cursor: 'pointer',
                      background:
                        idx === selectedScreenshotIndex ? '#e9f2ff' : '#fff',
                    }}
                    onClick={() => setSelectedScreenshotIndex(idx)}
                  >
                    <img
                      src={getScreenshotPreviewSrc(item)}
                      alt="Screenshot thumb"
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                      }}
                    />
                    <small className="d-block mt-1 text-truncate">
                      {item.timestamp
                        ? moment(item.timestamp)
                            .tz(selectedTimezone)
                            .format('hh:mm A')
                        : 'No time'}
                    </small>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
    </div>
  );
};
